import React, {useState, useRef, useEffect} from 'react';

import {BuildPath} from '../../../helpers/Paths';

import '../../../css/dropdown.scss';

interface DropdownProps {
    id?: string,
    className?:string
    defaultIcon?: [string, string]
    defaultValue?: string
    placeholder?: string
    setValue: (val: string | number | any) => void
    options: {icon?: string, iconAlt?: string, value: string | number | any, label: string}[]
    buttonText? : string | JSX.Element,
    icon?: JSX.Element
    isDisabled?:boolean
    canDeselectValue?:boolean,
    canReselectValue?:boolean
}

const Dropdown = ({
    id="",
    className='',
    placeholder='All',
    setValue,
    defaultIcon,
    defaultValue,
    options, 
    buttonText, 
    icon = <img className="dropdown-button-icon" src={`${BuildPath()}/images/insightsp2/DropDownIcon.svg`} aria-hidden alt=""/>,
    isDisabled,
    canDeselectValue=true,
    canReselectValue=false
}: DropdownProps) => {

    const [showList, setShowList] = useState<boolean>(false);
    const [selectedValue, setSelectedValue] = useState<string>();
    const [focusItem, setFocusItem] = useState<HTMLLIElement>();
    const [focusButton, setFocusButton] = useState(false);

    const dropDownMenu = useRef<HTMLUListElement>();
    const button = useRef<HTMLButtonElement>();

    useEffect(() => {
        return () => {
            setSelectedValue(undefined)
        }
    },[])
    
    useEffect(() => {
        setSelectedValue(defaultValue ? defaultValue : placeholder);
    }, [defaultValue])
    
    useEffect(()=> {
        if (focusItem) {
            focusItem.focus();
        }
        else {
            if(focusButton) {
                button.current.focus()
                setFocusButton(false)
            } 
        }
    },[focusItem])

    useEffect(() => {
        if (selectedValue && selectedValue !== placeholder) {
            // return the value associated with the selected label
            const valueObject = options.find(el => el.label === selectedValue)
            valueObject && setValue(valueObject.value) 
        }
        if (selectedValue && selectedValue === placeholder && selectedValue === defaultValue) {
            const valueObject = options.find(el => el.label === selectedValue)
            valueObject && setValue(valueObject.value) 
        }
        else if (selectedValue && selectedValue === placeholder) {
            // return the value associated with the selected label
            setValue(undefined)
        }
    }, [selectedValue])


    function openMenu(e: any) {
        e.preventDefault();
        setShowList(true);
        focusListNode(0);
    }

    function closeMenu(e: any) {
        e.preventDefault();
        setFocusButton(true);
        setShowList(false);
        setFocusItem(undefined); 
    }

    function focusListNode(increment: number, jump: 'start' | 'end' = undefined ) {
        if(!dropDownMenu.current) {
            setFocusItem(undefined);
            return;
        }
        const selectableElements = Array.from(dropDownMenu.current.children).filter(el => el.className !== 'title');
        if (jump) {
            jump === 'start'
                ? setFocusItem(selectableElements[0] as HTMLLIElement)
                : setFocusItem(selectableElements[selectableElements.length - 1] as HTMLLIElement)
                return;
        }
       
        const idx = focusItem ? selectableElements.findIndex((element)=> element ===  focusItem) : 0;
        setFocusItem(selectableElements[idx + increment] as HTMLLIElement);
    }

    function handleMenuKeyPress(e:React.KeyboardEvent) {
        switch(e.key) {
            case 'ArrowUp':
            case 'ArrowDown':
                e.preventDefault();
                openMenu(e);
                break;
            default: 
                break;
        }
    }

    function handleBlur(e: React.FocusEvent) {
        if(!e.currentTarget.closest('.dropdown-wrapper').contains(e.relatedTarget as Node)) {
            setFocusItem(undefined);
            setShowList(false);
        }
    }

    function handleInMenuKeyPress(e:React.KeyboardEvent) {
        const selectableElements = Array.from(dropDownMenu.current.children).filter(el => el.className !== 'title');
        const idx = selectableElements.findIndex((element)=> element ===  focusItem);
        switch(e.key){
            case 'ArrowUp':
                if (idx >= 1) {
                   focusListNode(-1);
                }
                break;
            case 'ArrowDown':
                if (idx + 1  < selectableElements.length ) {
                    focusListNode(1);
                } 
                break;
            case ' ':
            case 'Enter':
                selectOption(focusItem.textContent);
                setShowList(false);
                break;
            case 'Escape':
                setShowList(false);
                break;
            case 'Home':
                focusListNode(0, 'start');
                break;
            case 'End':
                focusListNode(0, 'end');        
                break;
            default:
                const alphaSearchResult = e.key.match(/^[a-zA-Z0-9]*$/) && selectableElements.find(el => el.textContent[0].toLowerCase() === e.key.toLowerCase())
                alphaSearchResult && setFocusItem(alphaSearchResult as HTMLLIElement);
        }
    }

    function selectOption (value: string) {
        setShowList(false);
        setFocusItem(undefined);
        setFocusButton(true);
       
        if (value === selectedValue) {
            if (canDeselectValue) {
                // Deselect the value
                setSelectedValue(placeholder);
            }
            if (canReselectValue) {
                // Trigger setValue explicitly on re-selection
                const valueObject = options.find(el => el.label === value);
                valueObject && setValue(valueObject.value);
            }
        } else {
            setSelectedValue(value);
        }
    }

    return (<div className={`dropdown-wrapper ${className}`}>
        <button className="dropdown-button"
            id={id}
            aria-haspopup="listbox"
            aria-expanded={showList}
            onClick={(e) => showList ? closeMenu(e): openMenu(e)}
            onKeyDown={handleMenuKeyPress}
            onBlur={handleBlur}
            ref={button}
            disabled={options.length === 0 || isDisabled}
        >
            {defaultIcon && <img className="dropdown-icon" src={defaultIcon[0]} alt={defaultIcon[1]} />}
            <span>{ buttonText ? buttonText : selectedValue }</span>
            {icon}
        </button>
        {showList && 
            <ul 
                ref={dropDownMenu}
                className={`dropdown-options`}
                role="listbox"
                aria-labelledby="dropdown-options"  
            >
                {options.map((option, idx) => 
                    
                    <li
                        key={`grouptitle-${placeholder}-option-${idx+1}`}
                        role="option"
                        tabIndex={-1}
                        className={option.label === selectedValue ? 'selected' : ''}
                        onClick={()=>selectOption(option.label)}
                        onKeyDown={handleInMenuKeyPress}
                        onBlur={handleBlur}
                        aria-selected={option.label === selectedValue}
                    >
                        {option?.icon && <img className="option-icon" src={option.icon} alt={option?.iconAlt}/>}
                        {option.label}
                    </li>
                  
                )}
            </ul>
        }
    </div>
)}

export default Dropdown;